import React, { useCallback, useEffect, useRef, useState } from "react";
import { getSchema, runQuery } from "../../Services/DataIntelligence";
import {
    generateDataIntelligenceSql,
    generateDataIntelligenceExplanation,
    testGeminiKey
} from "../../Services/Gemini";
import Loader from "../../Layouts/Loader";
import {
    DATA_INTELLIGENCE_STRINGS,
    getDataIntelligenceStorageKey
} from "./strings";

const formatSchemaForPrompt = (tables) => {
    if (!Array.isArray(tables) || tables.length === 0) return "";
    return tables
        .map(
            (t) =>
                `Table ${t.name}: ${(t.columns || []).map((c) => `${c.name} (${c.type || ""})`).join(", ")}`
        )
        .join("\n");
};

const escapeCsvCell = (value) => {
    if (value == null) return "";
    const str = typeof value === "object" ? JSON.stringify(value) : String(value);
    if (/[",\n\r]/.test(str)) {
        return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
};

const rowsToCsv = (columns, rows) => {
    const header = columns.map(escapeCsvCell).join(",");
    const body = rows.map((row) => columns.map((col) => escapeCsvCell(row[col])).join(","));
    return [header, ...body].join("\r\n");
};

const downloadCsv = (columns, rows, filename = "data-intelligence-export.csv") => {
    const csv = rowsToCsv(columns, rows);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const downloadQueryTxt = (request, query, filename = "data-intelligence-query.txt") => {
    const content = [
        "data intelligence request:",
        request.trim() || "(no question)",
        "",
        "query:",
        query.trim()
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const formatCellDisplay = (value) => {
    if (value == null) return "";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
};

const DataIntelligence = () => {
    const [apiKey, setApiKey] = useState("");
    const [rememberKey, setRememberKey] = useState(true);
    const [schema, setSchema] = useState(null);
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [schemaError, setSchemaError] = useState(null);
    const [question, setQuestion] = useState("");
    const [generatedSql, setGeneratedSql] = useState("");
    const [sqlLoading, setSqlLoading] = useState(false);
    const [sqlError, setSqlError] = useState(null);
    const [rows, setRows] = useState([]);
    const [runLoading, setRunLoading] = useState(false);
    const [runError, setRunError] = useState(null);
    const [keyTestResult, setKeyTestResult] = useState(null);
    const [keyTestLoading, setKeyTestLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [voiceError, setVoiceError] = useState(null);
    const [generateAndRunLoading, setGenerateAndRunLoading] = useState(false);
    const [showSchema, setShowSchema] = useState(false);
    const [explanation, setExplanation] = useState("");
    const [explanationLoading, setExplanationLoading] = useState(false);
    const recognitionRef = useRef(null);

    const storageKey = getDataIntelligenceStorageKey();

    const setRunErrorFromResponse = (err) => {
        const status = err?.response?.status;
        const msg = (err?.response?.data?.message || "").toLowerCase();
        if (status === 503) setRunError(DATA_INTELLIGENCE_STRINGS.errorNotConfigured);
        else if (status === 429) {
            setRunError(msg.includes("in progress") ? DATA_INTELLIGENCE_STRINGS.errorQueriesInProgress : DATA_INTELLIGENCE_STRINGS.errorRateLimit);
        } else if (status === 400 && msg.includes("select")) setRunError(DATA_INTELLIGENCE_STRINGS.errorOnlySelect);
        else if (status === 400 && msg.includes("timed out")) setRunError(DATA_INTELLIGENCE_STRINGS.errorQueryTimeout);
        else if (status === 400 && msg.includes("too long")) setRunError(DATA_INTELLIGENCE_STRINGS.errorSqlTooLong);
        else setRunError(DATA_INTELLIGENCE_STRINGS.errorRunFailed);
    };

    const getSpeechRecognition = () => {
        if (typeof window === "undefined") return null;
        return window.SpeechRecognition || window.webkitSpeechRecognition || null;
    };

    const startListening = () => {
        const SpeechRecognition = getSpeechRecognition();
        if (!SpeechRecognition) {
            setVoiceError(DATA_INTELLIGENCE_STRINGS.voiceUnsupported);
            return;
        }
        setVoiceError(null);
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = "en-US";
        recognition.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setQuestion((prev) => (prev ? prev.trimEnd() + " " + transcript : transcript));
        };
        recognition.onend = () => setIsListening(false);
        recognition.onerror = () => {
            setIsListening(false);
            setVoiceError(DATA_INTELLIGENCE_STRINGS.voiceError);
        };
        try {
            recognition.start();
            setIsListening(true);
            recognitionRef.current = recognition;
        } catch (err) {
            setVoiceError(DATA_INTELLIGENCE_STRINGS.voiceError);
        }
    };

    const stopListening = () => {
        if (recognitionRef.current) {
            try {
                recognitionRef.current.stop();
            } catch (_) {
                /* ignore */
            }
            recognitionRef.current = null;
        }
        setIsListening(false);
    };

    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) setApiKey(stored);
        } catch (_) {
            /* ignore */
        }
    }, [storageKey]);

    const fetchSchema = useCallback(() => {
        setSchemaLoading(true);
        setSchemaError(null);
        getSchema()
            .then((res) => {
                const data = res.data?.data?.responseData;
                const tables = data?.tables ?? [];
                setSchema(tables);
            })
            .catch((err) => {
                const status = err?.response?.status;
                const msg =
                    status === 503
                        ? DATA_INTELLIGENCE_STRINGS.errorNotConfigured
                        : status === 429
                            ? DATA_INTELLIGENCE_STRINGS.errorSchemaRateLimit
                            : DATA_INTELLIGENCE_STRINGS.errorSchemaFailed;
                setSchemaError(msg);
                setSchema(null);
            })
            .finally(() => setSchemaLoading(false));
    }, []);

    useEffect(() => {
        fetchSchema();
    }, [fetchSchema]);

    const saveOrClearKey = (key) => {
        try {
            if (rememberKey && key) {
                localStorage.setItem(storageKey, key);
            } else {
                localStorage.removeItem(storageKey);
            }
        } catch (_) {
            /* ignore */
        }
    };

    const handleTestKey = () => {
        const key = apiKey.trim();
        // Allow blank to use default Gemini key from the service
        saveOrClearKey(key);
        setKeyTestLoading(true);
        setKeyTestResult(null);
        setSqlError(null);
        testGeminiKey(key || undefined)
            .then((result) => {
                setKeyTestResult(result);
            })
            .catch(() => {
                setKeyTestResult({ ok: false });
            })
            .finally(() => setKeyTestLoading(false));
    };

    const handleGenerateSql = () => {
        const key = apiKey.trim();
        if (!question.trim()) {
            setSqlError(DATA_INTELLIGENCE_STRINGS.errorMissingQuestion);
            return;
        }
        saveOrClearKey(key);
        setSqlError(null);
        setSqlLoading(true);
        setGeneratedSql("");
        const schemaText = formatSchemaForPrompt(schema || []);
        generateDataIntelligenceSql(schemaText, question.trim(), key || undefined)
            .then((sql) => setGeneratedSql(sql))
            .catch(() => {
                setSqlError("Failed to generate SQL.");
            })
            .finally(() => setSqlLoading(false));
    };

    const handleRunQuery = () => {
        if (!generatedSql.trim()) return;
        setExplanation("");
        setRunError(null);
        setRunLoading(true);
        setRows([]);
        runQuery(generatedSql)
            .then((res) => {
                const data = res.data?.data?.responseData;
                setRows(data?.rows ?? []);
            })
            .catch(setRunErrorFromResponse)
            .finally(() => setRunLoading(false));
    };

    const handleExplainResult = () => {
        const key = apiKey.trim();
        if (!generatedSql.trim() || rows.length === 0) {
            return;
        }
        setExplanation("");
        setExplanationLoading(true);
        const resultSummary = JSON.stringify({
            rowCount: rows.length,
            columns,
            sampleRows: rows.slice(0, 10)
        });
        generateDataIntelligenceExplanation(generatedSql, resultSummary, key || undefined)
            .then((text) => {
                setExplanation(text || "");
            })
            .catch(() => {
                setExplanation("");
            })
            .finally(() => setExplanationLoading(false));
    };

    const handleGenerateSqlAndRun = () => {
        const key = apiKey.trim();
        if (!question.trim()) {
            setSqlError(DATA_INTELLIGENCE_STRINGS.errorMissingQuestion);
            return;
        }
        saveOrClearKey(key);
        setSqlError(null);
        setRunError(null);
        setGenerateAndRunLoading(true);
        setGeneratedSql("");
        setRows([]);
        const schemaText = formatSchemaForPrompt(schema || []);
        let sqlGenerated = false;
        generateDataIntelligenceSql(schemaText, question.trim(), key)
            .then((sql) => {
                sqlGenerated = true;
                setGeneratedSql(sql);
                return runQuery(sql);
            })
            .then((res) => {
                const data = res?.data?.data?.responseData;
                setRows(data?.rows ?? []);
            })
            .catch((err) => {
                if (!sqlGenerated) {
                    setSqlError("Failed to generate SQL.");
                } else {
                    setRunErrorFromResponse(err);
                }
            })
            .finally(() => setGenerateAndRunLoading(false));
    };

    const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

    return (
        <>
            <div className="page_header mb-3">
                <h2 className="mb-0">{DATA_INTELLIGENCE_STRINGS.pageTitle}</h2>
            </div>

            {schemaError && (
                <div className="alert alert-warning" role="alert">
                    {schemaError}
                </div>
            )}

            <div className="card mb-3">
                <div className="card-body">
                    <div className="mb-3">
                        <label className="form-label">{DATA_INTELLIGENCE_STRINGS.apiKeyLabel}</label>
                        <div className="d-flex gap-2 align-items-center flex-wrap">
                            <input
                                type="password"
                                className="form-control"
                                style={{ maxWidth: 320 }}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder={DATA_INTELLIGENCE_STRINGS.apiKeyPlaceholder}
                            />
                            <div className="form-check mb-0">
                                <input
                                    id="remember-key"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={rememberKey}
                                    onChange={(e) => setRememberKey(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="remember-key">
                                    {DATA_INTELLIGENCE_STRINGS.rememberKeyLabel}
                                </label>
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleTestKey}
                                disabled={keyTestLoading}
                            >
                                {keyTestLoading ? "Testing..." : "Test Key"}
                            </button>
                        </div>
                        <small className="text-muted">{DATA_INTELLIGENCE_STRINGS.apiKeyHelper}</small>
                        {keyTestResult && (
                            <div className="mt-1 small">
                                {keyTestResult.ok ? (
                                    <span className="text-success">Key is valid.</span>
                                ) : (
                                    <span className="text-danger">Key test failed.</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mb-2 d-flex align-items-center flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={fetchSchema}
                            disabled={schemaLoading}
                        >
                            {schemaLoading ? DATA_INTELLIGENCE_STRINGS.loadingSchema : DATA_INTELLIGENCE_STRINGS.schemaButton}
                        </button>
                        {schema && !schemaLoading && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => setShowSchema((s) => !s)}
                                >
                                    {showSchema ? DATA_INTELLIGENCE_STRINGS.hideSchemaButton : DATA_INTELLIGENCE_STRINGS.showSchemaButton}
                                </button>
                                <div className="d-flex align-items-center flex-wrap gap-1">
                                    <span className="text-muted small">Legend</span>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => {
                                            const sql = `
SELECT 
  *
FROM app_type;
`.trim();
                                            setGeneratedSql(sql);
                                            setRunError(null);
                                            setRunLoading(true);
                                            setRows([]);
                                            runQuery(sql)
                                                .then((res) => {
                                                    const data = res.data?.data?.responseData;
                                                    setRows(data?.rows ?? []);
                                                })
                                                .catch(setRunErrorFromResponse)
                                                .finally(() => setRunLoading(false));
                                        }}
                                    >
                                        app_type values
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => {
                                            const sql = `
SELECT 
  *
FROM event_type;
`.trim();
                                            setGeneratedSql(sql);
                                            setRunError(null);
                                            setRunLoading(true);
                                            setRows([]);
                                            runQuery(sql)
                                                .then((res) => {
                                                    const data = res.data?.data?.responseData;
                                                    setRows(data?.rows ?? []);
                                                })
                                                .catch(setRunErrorFromResponse)
                                                .finally(() => setRunLoading(false));
                                        }}
                                    >
                                        event_type values
                                    </button>
                                </div>
                                <span className="text-muted small">
                                    {schema.length} table(s) loaded
                                </span>
                            </>
                        )}
                    </div>
                    {showSchema && schema && schema.length > 0 && (
                        <div className="mb-3 p-3 bg-light rounded font-monospace small">
                            {schema.map((t) => (
                                <div key={t.name} className="mb-2">
                                    <strong>{t.name}</strong>
                                    <div className="ms-3 text-muted">
                                        {(t.columns || []).map((c) => (
                                            <div key={c.name}>
                                                {c.name} ({c.type || "—"})
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div>
                        <label className="form-label mb-1">{DATA_INTELLIGENCE_STRINGS.questionLabel}</label>
                        <div className="mb-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={isListening ? stopListening : startListening}
                                disabled={!getSpeechRecognition()}
                                title={!getSpeechRecognition() ? DATA_INTELLIGENCE_STRINGS.voiceUnsupported : isListening ? DATA_INTELLIGENCE_STRINGS.listenStopButton : DATA_INTELLIGENCE_STRINGS.listenButton}
                            >
                                {isListening ? DATA_INTELLIGENCE_STRINGS.listeningButton + " " + DATA_INTELLIGENCE_STRINGS.listenStopButton : DATA_INTELLIGENCE_STRINGS.listenButton}
                            </button>
                        </div>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={question}
                            onChange={(e) => { setQuestion(e.target.value); setVoiceError(null); }}
                            placeholder={DATA_INTELLIGENCE_STRINGS.questionPlaceholder}
                        />
                        {voiceError && (
                            <small className="text-danger d-block mt-1">{voiceError}</small>
                        )}
                    </div>
                    <div className="mt-2 d-flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleGenerateSql}
                            disabled={sqlLoading || runLoading || generateAndRunLoading || !schema?.length || schemaLoading}
                        >
                            {sqlLoading ? DATA_INTELLIGENCE_STRINGS.generatingSqlButton : DATA_INTELLIGENCE_STRINGS.generateSqlButton}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleGenerateSqlAndRun}
                            disabled={sqlLoading || runLoading || generateAndRunLoading || !schema?.length || schemaLoading}
                        >
                            {generateAndRunLoading ? DATA_INTELLIGENCE_STRINGS.generatingSqlAndRunButton : DATA_INTELLIGENCE_STRINGS.generateSqlAndRunButton}
                        </button>
                    </div>
                    {sqlError && (
                        <div className="alert alert-danger mt-2 mb-0" role="alert">
                            {sqlError}
                        </div>
                    )}
                </div>
            </div>

            <div className="card mb-3">
                <div className="card-body">
                    <label className="form-label text-muted small">{DATA_INTELLIGENCE_STRINGS.sqlLabel}</label>
                    <textarea
                        className="form-control font-monospace small"
                        rows={8}
                        value={generatedSql}
                        onChange={(e) => setGeneratedSql(e.target.value)}
                        placeholder={DATA_INTELLIGENCE_STRINGS.sqlPlaceholder}
                        spellCheck={false}
                    />
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleRunQuery}
                            disabled={runLoading || !generatedSql.trim()}
                        >
                            {runLoading ? DATA_INTELLIGENCE_STRINGS.runningButton : DATA_INTELLIGENCE_STRINGS.runQueryButton}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => downloadQueryTxt(question, generatedSql)}
                            disabled={!generatedSql.trim()}
                        >
                            {DATA_INTELLIGENCE_STRINGS.saveQueryButton}
                        </button>
                        {runLoading && (
                            <span className="text-muted small">{DATA_INTELLIGENCE_STRINGS.queryMayTakeUpTo}</span>
                        )}
                    </div>
                    {runError && (
                        <div className="alert alert-danger mt-2 mb-0" role="alert">
                            {runError}
                        </div>
                    )}
                </div>
            </div>

            {rows.length > 0 && (
                <div className="card mb-3">
                    <div className="card-body">
                        <h6 className="text-muted mb-2">
                            {DATA_INTELLIGENCE_STRINGS.resultsLabel} ({rows.length})
                        </h6>
                        <div className="table-responsive">
                            <table className="table table-striped table-sm">
                                <thead>
                                    <tr>
                                        {columns.map((col) => (
                                            <th key={col}>{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => (
                                        <tr key={i}>
                                            {columns.map((col) => (
                                                <td key={col}>
                                                    {formatCellDisplay(row[col])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => downloadCsv(columns, rows)}
                            >
                                {DATA_INTELLIGENCE_STRINGS.downloadCsvButton}
                            </button>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleExplainResult}
                                disabled={explanationLoading}
                            >
                                {explanationLoading
                                    ? DATA_INTELLIGENCE_STRINGS.explainingResultButton
                                    : DATA_INTELLIGENCE_STRINGS.explainResultButton}
                            </button>
                        </div>
                        {explanation && (
                            <div className="mt-3">
                                <div className="fw-bold mb-1">Explanation</div>
                                <div className="small text-muted" style={{ whiteSpace: "pre-wrap" }}>
                                    {explanation}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {schemaLoading && !schema && (
                <div className="d-flex align-items-center gap-2 text-muted">
                    <Loader />
                    <span className="small">{DATA_INTELLIGENCE_STRINGS.loadingSchema}</span>
                </div>
            )}
        </>
    );
};

export default DataIntelligence;
